import { useState } from 'react';

interface ReservationSectionProps {
  isModalOpen: boolean;
  onOpenModal: () => void;
  onCloseModal: () => void;
}

export default function ReservationSection({ isModalOpen, onOpenModal, onCloseModal }: ReservationSectionProps) {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    fecha: '',
    hora: '',
    fechaVuelta: '',
    horaVuelta: '',
    origen: '',
    destino: '',
    pasajeros: '',
    servicio: '',
    tipoViaje: '',
    numeroVuelo: '',
    comentarios: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5pvaeb592clg457k8n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          fecha: '',
          hora: '',
          fechaVuelta: '',
          horaVuelta: '',
          origen: '',
          destino: '',
          pasajeros: '',
          servicio: '',
          tipoViaje: '',
          numeroVuelo: '',
          comentarios: ''
        });
        
        setTimeout(() => {
          onCloseModal();
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section 
        id="reservas" 
        className="relative py-32 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/506e39e0230b4b8ccc805e36ab06e874.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        
        <div className="relative z-10 text-center">
          <h2 className="text-7xl font-bold text-white leading-tight mb-6 tracking-wide">
            RESERVA TU<br />
            MICROBÚS<br />
            AHORA
          </h2>
          <p className="text-2xl text-white mb-8">
            Proceso simple y rápido<br />
            Confirmación inmediata
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
            <a
              href="tel:+34614932797"
              className="bg-white text-black py-4 px-8 rounded-lg flex items-center space-x-3 hover:bg-gray-100 transition-colors text-lg font-semibold cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-phone-line text-2xl"></i>
              <span>+34 614 932 797</span>
            </a>
            
            <a
              href="https://wa.me/34614932797"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white py-4 px-8 rounded-lg flex items-center space-x-3 hover:bg-green-600 transition-colors text-lg font-semibold cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      {/* Modal de Reserva */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-8 relative my-4 max-h-[95vh] overflow-y-auto">
            <button
              onClick={onCloseModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 cursor-pointer z-10"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            
            <div className="mb-6">
              <h3 className="text-3xl font-bold mb-2">Reserva tu Microbús</h3>
              <p className="text-gray-600">
                Completa el formulario y te contactaremos para confirmar tu reserva
              </p>
            </div>
            
            <form id="reservation-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Tu nombre completo"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono *
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
                      +34
                    </div>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="612 345 678"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de servicio *
                  </label>
                  <select
                    name="servicio"
                    value={formData.servicio}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="Traslado Aeropuerto">Traslado Aeropuerto</option>
                    <option value="Eventos y Celebraciones">Eventos y Celebraciones</option>
                    <option value="Excursiones">Excursiones</option>
                    <option value="Transporte Empresarial">Transporte Empresarial</option>
                    <option value="Traslado Golf">Traslado Golf</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tipo de viaje *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, tipoViaje: 'Solo Ida' }))}
                    className={`px-6 py-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap text-sm font-medium ${
                      formData.tipoViaje === 'Solo Ida'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-orange-300'
                    }`}
                  >
                    <i className="ri-arrow-right-line mr-2"></i>
                    Solo Ida
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, tipoViaje: 'Ida y Vuelta' }))}
                    className={`px-6 py-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap text-sm font-medium ${
                      formData.tipoViaje === 'Ida y Vuelta'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-orange-300'
                    }`}
                  >
                    <i className="ri-arrow-left-right-line mr-2"></i>
                    Ida y Vuelta
                  </button>
                </div>
                {!formData.tipoViaje && (
                  <p className="text-xs text-red-500 mt-2">
                    Por favor selecciona el tipo de viaje
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de ida *
                  </label>
                  <input
                    type="date"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hora de ida *
                  </label>
                  <input
                    type="time"
                    name="hora"
                    value={formData.hora}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {formData.tipoViaje === 'Ida y Vuelta' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-orange-50 p-6 rounded-lg border border-orange-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de vuelta *
                    </label>
                    <input
                      type="date"
                      name="fechaVuelta"
                      value={formData.fechaVuelta}
                      onChange={handleInputChange}
                      required
                      min={formData.fecha}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hora de vuelta *
                    </label>
                    <input
                      type="time"
                      name="horaVuelta"
                      value={formData.horaVuelta}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-map-pin-line text-orange-500 mr-2"></i>
                    Origen *
                  </label>
                  <input
                    type="text"
                    name="origen"
                    value={formData.origen}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Ej: Aeropuerto de Málaga, hotel, dirección..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-map-pin-line text-orange-500 mr-2"></i>
                    Destino *
                  </label>
                  <input
                    type="text"
                    name="destino"
                    value={formData.destino}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Ej: Hotel, restaurante, dirección..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de pasajeros *
                  </label>
                  <input
                    type="number"
                    name="pasajeros"
                    value={formData.pasajeros}
                    onChange={handleInputChange}
                    required
                    min="1"
                    max="19"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Hasta 19 pasajeros"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de vuelo
                  </label>
                  <input
                    type="text"
                    name="numeroVuelo"
                    value={formData.numeroVuelo}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="Ej: IB2425, VY1234"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Opcional - Para traslados aeroportuarios
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comentarios adicionales
                </label>
                <textarea
                  name="comentarios"
                  value={formData.comentarios}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  placeholder="Información adicional sobre tu reserva (máximo 500 caracteres)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.comentarios.length}/500 caracteres
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                  <span className="text-green-700 text-sm">
                    ¡Reserva enviada! Te contactaremos pronto a wearebusmalaga@gmail.com
                  </span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                  <span className="text-red-700 text-sm">
                    Error al enviar. Por favor, intenta de nuevo o llama al 614 932 797
                  </span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !formData.tipoViaje}
                className="w-full bg-orange-500 text-white py-4 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-lg"
              >
                {isSubmitting ? 'Enviando...' : 'ENVIAR RESERVA'}
              </button>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center mb-3">
                  O contáctanos directamente:
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <a
                    href="tel:614932797"
                    className="bg-black text-white py-3 px-6 rounded-lg flex items-center space-x-2 hover:bg-gray-800 transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    <span>614 932 797</span>
                  </a>
                  
                  <a
                    href="https://wa.me/34614932797"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-500 text-white py-3 px-6 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line"></i>
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}